import {
  IconButton,
  makeStyles,
  Typography,
  Button as MaterialButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { useSnackbar } from 'notistack';
import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { Field, Form as FinalForm, useField, useForm } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import * as yup from 'yup';
import { CustomQuestion } from '../../api/CustomQuestion/types/CustomQuestion';
import { CustomQuestionType } from '../../api/CustomQuestion/types/Type';
import { ConsentType } from '../../api/auth';
import { EnrichedVentureResponse } from '../../api/enrich';
import foundersAPI, { FounderName } from '../../api/founders';
import { TenantId } from '../../api/tenants/Tenant';
import { Venture, VentureId } from '../../api/ventures/types/Venture';
import { ExportIcon } from '../../assets/icons';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { COLORS } from '../../theme/variables';
import { getFormattedGeneralReportDate } from '../../utils/date';
import {
  DEDICATION,
  DONE_ALREADY,
  GENDER_VENTURE,
  GROWTH_RATIO,
  lengthField,
  MARKET_SIZE,
  RACE,
  RADIO_SELECT,
  REASON_TO_START_NOW,
  SUPPORT_INTERESTED_RECEIVING,
  VENTURE_STAGE_OPTIONS,
} from '../../utils/form';
import { checkSizeTablet, createMarkup, uuidv4 } from '../../utils/functions';
import { length, getPlainText } from '../../utils/wysiwyg';
import { yupValidate } from '../../utils/yup';
import {
  Button,
  CheckboxListOther,
  ConfirmButton,
  FormGroup,
  PageLoader,
  SnackMessage,
  Text,
  Avatar,
  AttachFilesButton,
  ModalV2,
  StepHeader,
} from '../common';
import AttachmentCard, {
  Attachment,
  getAttachmentFromFile,
} from '../common/attachment-card';
import PrefillModal from '../common/prefill-modal';
import Wizard, {
  FormValues as VentureFormValues,
} from '../common/venture-wizard';
import { ValidFormValues } from '../common/wizard';
import CustomFormField from './custom-form/custom-form-field';
import {
  FormCheckbox,
  FormComboBox,
  FormDateInput,
  FormRadioboxGroup,
  FormSelect,
  FormSpecializationInput,
  TextFieldWrapper,
  TextFieldWysiwyg,
} from './wrappers';

const useStyles = makeStyles((theme) => ({
  title: {
    marginBottom: 32,
  },
  sectionBlock: {
    '& + &': {
      marginTop: 56,
    },
  },
  goalsBlock: {
    '& + &': {
      marginTop: 32,
    },
  },
  intro: {
    display: 'block',
    marginBottom: 20,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  btn_footer: {
    marginTop: 20,
  },
  founderName: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 32,
    '& $title': {
      marginBottom: 0,
    },
  },
  deleteFounderIcon: {
    marginLeft: 10,
  },
  consent: {
    marginBottom: 20,
  },
  checkboxLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
    },
  },
  loader: {
    margin: '100px 0',
  },
  founderNamesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    maxWidth: 500,
  },
  uploadPhotoWrapper: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  attachmentsList: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '20px 0 -16px -16px',
  },
  attachment: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0 0 16px 16px',

    [theme.breakpoints.up(800)]: {
      width: '50%',
    },
  },
  aboutField: {
    minHeight: 64,
  },
  descriptionField: {
    minHeight: 64,
  },
  educationField: {
    flex: '7 1 0 !important',
    minWidth: 0,
    '& > *': {
      width: '100%',
    },
  },
  serialFounderField: {
    flex: '3 1 0 !important',
    minWidth: 0,
    '& > *': {
      width: '100%',
    },
  },
  goalsTable: {
    maxWidth: '100%',
    marginBottom: 32,
  },
  goalRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
  },
  goalName: {
    flex: 1,
    marginRight: 10,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  goalActions: {
    display: 'flex',
    gap: 4,
  },
  goalModalContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  goalModalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16,
  },
  goalDescription: {
    marginBottom: 32,
  },
  goalButton: {
    marginBottom: 16,
  },
}));

interface Props {
  onSubmit: (values: ValidFormValues) => void;
  tenantId: TenantId;
  isLoading: boolean;
  consents: ConsentType[];
  showCustomAdditionalInfo?: boolean;
  additionalInfoCustomQuestions?: CustomQuestion[] | null;
  intro: string;
  venture: Venture | null;
  showSuccess: () => void;
  noteAttachments: Attachment[];
  handleNoteFileUpload: (file: File) => Promise<void>;
  handleNoteFileRemove: (index: number) => void;
  createNote: () => Promise<void>;
  attachments: Map<string, Attachment>;
  setAttachments: React.Dispatch<React.SetStateAction<Map<string, Attachment>>>;
  files: Map<string, File>;
  setFiles: React.Dispatch<React.SetStateAction<Map<string, File>>>;
  founderCommitmentEnabled?: boolean;
  goalIntegrationEnabled?: boolean;
  showFounderCustomQuestions?: boolean;
  founderCustomQuestions?: CustomQuestion[] | null;
  founderAttachments: Map<string, Attachment>;
  setFounderAttachments: React.Dispatch<
    React.SetStateAction<Map<string, Attachment>>
  >;
  founderFiles: Map<string, File>;
  setFounderFiles: React.Dispatch<React.SetStateAction<Map<string, File>>>;
  showPrefillModal?: boolean;
  onPrefillComplete?: (data: EnrichedVentureResponse) => void;
  onPrefillDismiss?: () => void;
  prefillData?: Partial<VentureFormValues> | null;
}

interface GoalFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    title: string;
    description: string;
    startDate: string | null;
    targetDate: string | null;
  }) => void;
  initialData: WizardGoal | null;
  tenantId: TenantId;
}

const goalValidationSchema = yup.object().shape({
  title: yup.string().max(250).required(),
  startDate: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === '' || originalValue == null ? null : value;
    }),
  targetDate: yup
    .date()
    .required()
    .min(
      new Date(new Date().setHours(0, 0, 0, 0)),
      'Target Date cannot be in the past',
    )
    .test(
      'isAfterStart',
      'Start Date must be prior to Completion Date',
      function (value) {
        const { startDate } = this.parent;
        if (!startDate) return true;
        return new Date(value) > new Date(startDate);
      },
    ),
  description: yup
    .string()
    .test('isEmpty', 'Required', (value) => {
      if (!value) return false;
      const plainText = getPlainText(value);
      return plainText.trim().length > 0;
    })
    .test(
      'max-html',
      `Max ${lengthField.wysiwygMaxLength} characters (HTML)`,
      (value) => {
        return !value || value.length <= lengthField.wysiwygMaxLength;
      },
    )
    .test(
      'max-text',
      `Max ${lengthField.wysiwygDisplayLimit} characters`,
      (value) => {
        return !value || length(value) <= lengthField.wysiwygDisplayLimit;
      },
    ),
});

const GoalFormModal = ({
  open,
  onClose,
  onSave,
  initialData,
}: GoalFormModalProps) => {
  const classes = useStyles();
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (open) {
      setIsEditMode(!!initialData);
    }
  }, [open, initialData]);

  const handleSubmit = (values: any) => {
    onSave({
      title: values.title,
      description: values.description,
      startDate: values.startDate || null,
      targetDate: values.targetDate || null,
    });
    onClose();
  };

  if (!open) return null;

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      width='600px'
      titleProps={{ title: isEditMode ? 'Edit Goal' : 'Create Goal' }}
    >
      <FinalForm
        key={initialData?.id || 'new'}
        validate={yupValidate(goalValidationSchema)}
        onSubmit={handleSubmit}
        initialValues={{
          title: initialData?.title || '',
          startDate: initialData?.startDate || null,
          targetDate: initialData?.targetDate || null,
          description: initialData?.description || '',
        }}
        render={({ handleSubmit: onFormSubmit, submitting }) => (
          <form
            noValidate
            onSubmit={onFormSubmit}
            className={classes.goalModalContent}
          >
            <FormGroup>
              <Field<string>
                name='title'
                component={TextFieldWrapper}
                label='Title*'
                InputProps={{
                  inputProps: {
                    maxLength: 250,
                  },
                }}
              />
            </FormGroup>
            <FormGroup>
              <Field<string>
                name='startDate'
                component={FormDateInput}
                label='Start Date'
                editable
                parse={(value) => {
                  try {
                    if (value) {
                      return getFormattedGeneralReportDate(value);
                    }
                    return '';
                  } catch (e: any) {
                    return 'invalid';
                  }
                }}
              />
            </FormGroup>
            <FormGroup>
              <Field<string>
                name='targetDate'
                component={FormDateInput}
                label='Target Date*'
                editable
                parse={(value) => {
                  try {
                    if (value) {
                      return getFormattedGeneralReportDate(value);
                    }
                    return '';
                  } catch (e: any) {
                    return 'invalid';
                  }
                }}
              />
            </FormGroup>
            <FormGroup>
              <Field<string>
                name='description'
                component={TextFieldWysiwyg}
                label='Description*'
                placeholder='Type here...'
                maxLength={lengthField.wysiwygDisplayLimit}
                showCount
              />
            </FormGroup>
            <div className={classes.goalModalActions}>
              <Button variant='outlined' onClick={onClose} type='button'>
                Cancel
              </Button>
              <Button type='submit' disabled={submitting}>
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        )}
      />
    </ModalV2>
  );
};

interface Step1Props {
  countSteps: number;
  stepNumber: number;
  intro: string;
  tenantId: TenantId;
}

const Step1 = ({ countSteps, stepNumber, intro, tenantId }: Step1Props) => {
  const classes = useStyles();

  return (
    <div>
      {intro && (
        <Text variant='normal' className={classes.intro}>
          {intro}
        </Text>
      )}
      <StepHeader
        title='Business Information'
        stepNumber={stepNumber}
        totalSteps={countSteps}
      />
      <div className={classes.sectionBlock}>
        <FormGroup>
          <Field<string>
            name='ventureName'
            component={TextFieldWrapper}
            label='Venture Name*'
            testid='venture-wizard-step1-ventureName'
          />
        </FormGroup>
        <FormGroup>
          <Field
            name='specializations'
            component={FormSpecializationInput}
            label='Specialization*'
            data-testid='venture-wizard-step1-specializations'
            tenantId={tenantId}
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='url'
            component={TextFieldWrapper}
            label='Website'
            placeholder='https://www.yourwebsite.com'
            testid='venture-wizard-step1-url'
          />
        </FormGroup>
        <FormGroup mobile={checkSizeTablet(800)}>
          <Field<string>
            testid='venture-wizard-step1-ventureStage'
            name='ventureStage'
            component={FormComboBox}
            label='Venture Stage'
            options={[...VENTURE_STAGE_OPTIONS]}
          />
          <Field<string>
            testid='venture-wizard-step1-businessModel'
            name='businessModel'
            component={TextFieldWrapper}
            label='Business Model'
            InputProps={{
              inputProps: {
                maxLength: lengthField.businessModel,
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            testid='venture-wizard-step1-description'
            name='description'
            component={TextFieldWrapper}
            label='Description'
            multiline
            InputProps={{
              inputProps: {
                className: classes.descriptionField,
                maxLength: lengthField.description,
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            testid='venture-wizard-step1-mentorshipNeeds'
            name='mentorshipNeeds'
            component={TextFieldWrapper}
            label='Mentorship Needs'
            multiline
            InputProps={{
              inputProps: {
                className: classes.descriptionField,
                maxLength: lengthField.mentorshipNeeds,
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            testid='venture-wizard-step1-matchingNotes'
            name='matchingNotes'
            component={TextFieldWrapper}
            label='Matching Notes'
            multiline
            InputProps={{
              inputProps: {
                className: classes.descriptionField,
                maxLength: lengthField.matchingNotes,
              },
            }}
          />
        </FormGroup>
      </div>
      <div className={classes.sectionBlock}>
        <Typography variant='h3' className={classes.title}>
          Address
        </Typography>
        <FormGroup>
          <Field<string>
            name='address'
            component={TextFieldWrapper}
            label='Address'
            testid='venture-wizard-step1-address'
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='city'
            component={TextFieldWrapper}
            label='City'
            testid='venture-wizard-step1-city'
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='state'
            component={TextFieldWrapper}
            label='State'
            testid='venture-wizard-step1-state'
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='zip'
            component={TextFieldWrapper}
            label='Zip code'
            testid='venture-wizard-step1-zip'
            InputProps={{
              inputProps: {
                maxLength: lengthField.zip,
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='country'
            component={TextFieldWrapper}
            label='Country'
            testid='venture-wizard-step1-country'
          />
        </FormGroup>
      </div>
    </div>
  );
};

interface FounderCustomQuestionFieldProps {
  question: CustomQuestion;
  founderTempId: string;
  founderAttachments: Map<string, Attachment>;
  onUploadFile: (
    founderTempId: string,
    questionId: string,
  ) => (file: File) => Promise<string>;
  onFileRemove: (founderTempId: string, questionId: string) => () => void;
}

const FounderCustomQuestionField = ({
  question,
  founderTempId,
  founderAttachments,
  onUploadFile,
  onFileRemove,
}: FounderCustomQuestionFieldProps) => {
  const fieldKey = `${founderTempId}_${question.id}`;
  const formFieldName = `founderCustom_${fieldKey}`;
  const { input, meta } = useField(formFieldName, {
    subscription: {
      value: true,
      error: true,
      submitFailed: true,
      dirtySinceLastSubmit: true,
      touched: true,
    },
  });

  const namespacedQuestion = { ...question, id: formFieldName };

  return (
    <CustomFormField
      question={namespacedQuestion}
      hasAccessToUpdate={true}
      onChange={input.onChange}
      value={input.value || ''}
      onUploadFile={
        question.type === CustomQuestionType.ATTACHMENT
          ? onUploadFile(founderTempId, question.id)
          : undefined
      }
      onFileRemove={
        question.type === CustomQuestionType.ATTACHMENT
          ? onFileRemove(founderTempId, question.id)
          : undefined
      }
      attachment={
        question.type === CustomQuestionType.ATTACHMENT
          ? founderAttachments.get(fieldKey)
          : undefined
      }
      error={meta.error}
      submitFailed={(meta.submitFailed && meta.dirtySinceLastSubmit) || (meta.touched && meta.error)}
    />
  );
};

interface Step2Props {
  countSteps: number;
  stepNumber: number;
  showFounderCustomQuestions?: boolean;
  founderCustomQuestions?: CustomQuestion[] | null;
  founderAttachments: Map<string, Attachment>;
  setFounderAttachments: React.Dispatch<
    React.SetStateAction<Map<string, Attachment>>
  >;
  founderFiles: Map<string, File>;
  setFounderFiles: React.Dispatch<React.SetStateAction<Map<string, File>>>;
}

const Step2 = ({
  countSteps,
  stepNumber,
  showFounderCustomQuestions,
  founderCustomQuestions,
  founderAttachments,
  setFounderAttachments,
  founderFiles,
  setFounderFiles,
}: Step2Props) => {
  const classes = useStyles();

  const formAPI = useForm();

  const handleRemoveFounder = (founderIndex: number) => () => {
    // Clean up custom question data for the removed founder
    const founder = formAPI.getState().values?.founders?.[founderIndex];
    const founderTempId = founder?.tempId;
    if (founderTempId && founderCustomQuestions?.length) {
      founderCustomQuestions.forEach((question) => {
        const key = `${founderTempId}_${question.id}`;
        formAPI.change(`founderCustom_${key}`, undefined);

        // Clean up attachments and files
        if (question.type === CustomQuestionType.ATTACHMENT) {
          const attachment = founderAttachments.get(key);
          if (attachment?.url && attachment.url.startsWith('blob:')) {
            URL.revokeObjectURL(attachment.url);
          }
          setFounderAttachments((prev) => {
            const newMap = new Map(prev);
            newMap.delete(key);
            return newMap;
          });
          setFounderFiles((prev) => {
            const newMap = new Map(prev);
            newMap.delete(key);
            return newMap;
          });
        }
      });
    }

    return formAPI.mutators.remove('founders', founderIndex);
  };

  const handleUploadFileForFounderQuestion = useCallback(
    (founderTempId: string, questionId: string) => {
      return async (file: File): Promise<string> => {
        const key = `${founderTempId}_${questionId}`;
        const attachment = getAttachmentFromFile(file);
        const tempUrl = URL.createObjectURL(file);
        attachment.url = tempUrl;

        setFounderFiles((prev) => {
          const newMap = new Map(prev);
          newMap.set(key, file);
          return newMap;
        });
        setFounderAttachments((prev) => {
          const newMap = new Map(prev);
          newMap.set(key, attachment);
          return newMap;
        });

        const tempMarker = `pending:${key}`;
        formAPI.change(`founderCustom_${key}`, tempMarker);
        return tempMarker;
      };
    },
    [formAPI, setFounderFiles, setFounderAttachments],
  );

  const handleFounderFileRemove = useCallback(
    (founderTempId: string, questionId: string) => {
      return () => {
        const key = `${founderTempId}_${questionId}`;
        setFounderFiles((prev) => {
          const newMap = new Map(prev);
          newMap.delete(key);
          return newMap;
        });
        setFounderAttachments((prev) => {
          const newMap = new Map(prev);
          newMap.delete(key);
          return newMap;
        });
        formAPI.change(`founderCustom_${key}`, '');
      };
    },
    [formAPI, setFounderFiles, setFounderAttachments],
  );

  const renderFields = (
    fields: FieldArrayRenderProps<
      { firstName: string; tempId?: string },
      any
    >['fields'],
  ) => {
    return fields.map((name, index) => {
      const founderTempId = fields.value[index]?.tempId || `founder-${index}`;

      return (
        <div
          key={fields.value[index]?.tempId || `${name}-${index}`}
          className={classes.sectionBlock}
        >
          <div className={classes.founderName}>
            <Typography variant='h3' className={classes.title}>
              Founder {index + 1}
            </Typography>
            {index !== 0 && (
              <IconButton
                className={classes.deleteFounderIcon}
                onClick={handleRemoveFounder(index)}
                data-testid={`venture-delete-founder-button-${index}`}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </div>
          <FormGroup mobile={checkSizeTablet(800)}>
            <Field<string>
              name={`${name}.firstName`}
              InputProps={{ maxLength: 250 }}
              component={TextFieldWrapper}
              label='First name*'
              testid={`venture-wizard-step2-founders_${index}-firstName`}
            />
            <Field<string>
              name={`${name}.lastName`}
              InputProps={{ maxLength: 250 }}
              component={TextFieldWrapper}
              label='Last name*'
              testid={`venture-wizard-step2-founders_${index}-lastName`}
            />
          </FormGroup>
          <FormGroup mobile={checkSizeTablet(800)}>
            <Field<string>
              name={`${name}.email`}
              component={TextFieldWrapper}
              label='Email*'
              formatOnBlur
              InputProps={{ maxLength: 250 }}
              format={(value: string) => {
                return value ? value.toLowerCase() : value;
              }}
              testid={`venture-wizard-step2-founders_${index}-email`}
            />
            <Field<string>
              name={`${name}.emailConfirm`}
              component={TextFieldWrapper}
              label='Email (confirm)*'
              formatOnBlur
              InputProps={{ maxLength: 250 }}
              format={(value: string) => {
                return value ? value.toLowerCase() : value;
              }}
              testid={`venture-wizard-step2-founders_${index}-emailConfirm`}
            />
          </FormGroup>
          <FormGroup mobile={checkSizeTablet(800)}>
            <Field<string>
              name={`${name}.phone`}
              label='Phone'
              InputProps={{ maxLength: 250 }}
              component={TextFieldWrapper}
              testid={`venture-wizard-step2-founders_${index}-phone`}
            />
            <Field<string>
              name={`${name}.linkedInProfile`}
              label='LinkedIn profile (link)'
              placeholder='https://www.linkedin.com/in/your-profile'
              component={TextFieldWrapper}
              InputProps={{ maxLength: 250 }}
              testid={`venture-wizard-step2-founders_${index}-linkedInProfile`}
            />
          </FormGroup>
          <FormGroup>
            <Field<string>
              testid={`venture-wizard-step2-founders_${index}-about`}
              name={`${name}.about`}
              component={TextFieldWrapper}
              label='About*'
              multiline
              InputProps={{
                inputProps: {
                  className: classes.aboutField,
                  maxLength: 512,
                },
              }}
            />
          </FormGroup>
          <FormGroup mobile={checkSizeTablet(800)}>
            <Field<string>
              name={`${name}.gender`}
              testid={`venture-wizard-step2-founders_${index}-gender`}
              component={FormSelect}
              label='Gender'
              options={GENDER_VENTURE}
            />
            <Field<string>
              name={`${name}.race`}
              testid={`venture-wizard-step2-founders_${index}-race`}
              component={FormSelect}
              label='Race'
              options={RACE}
            />
          </FormGroup>
          <FormGroup>
            <Field<string>
              name={`${name}.currentRole`}
              testid={`venture-wizard-step2-founders_${index}-currentRole`}
              component={TextFieldWrapper}
              label='Current Role'
              parse={(value: string) => (!value ? '' : value)}
              InputProps={{
                inputProps: {
                  maxLength: lengthField.currentRole,
                },
              }}
            />
          </FormGroup>
          <FormGroup mobile={checkSizeTablet(800)}>
            <div className={classes.educationField}>
              <Field<string>
                name={`${name}.education`}
                testid={`venture-wizard-step2-founders_${index}-education`}
                component={TextFieldWrapper}
                label='Education'
                parse={(value: string) => (!value ? '' : value)}
                InputProps={{
                  inputProps: {
                    maxLength: lengthField.education,
                  },
                }}
              />
            </div>
            <div className={classes.serialFounderField}>
              <Field<string>
                name={`${name}.isSerialFounder`}
                testid={`venture-wizard-step2-founders_${index}-isSerialFounder`}
                component={FormSelect}
                label='Serial Founder'
                options={RADIO_SELECT}
              />
            </div>
          </FormGroup>
          {showFounderCustomQuestions &&
            founderCustomQuestions?.map((question) => (
              <FounderCustomQuestionField
                key={`${founderTempId}-${question.id}`}
                question={question}
                founderTempId={founderTempId}
                founderAttachments={founderAttachments}
                onUploadFile={handleUploadFileForFounderQuestion}
                onFileRemove={handleFounderFileRemove}
              />
            ))}
        </div>
      );
    });
  };

  return (
    <div>
      <StepHeader
        title='Founder Information'
        stepNumber={stepNumber}
        totalSteps={countSteps}
      />
      <FieldArray name='founders'>
        {({
          fields,
        }: FieldArrayRenderProps<
          { firstName: string; tempId?: string },
          any
        >) => renderFields(fields)}
      </FieldArray>
      <div className={classes.btn_footer}>
        <Button
          type='button'
          variant='outlined'
          onClick={() =>
            formAPI.mutators.push('founders', {
              firstName: '',
              active: true,
              tempId: uuidv4(),
            })
          }
          data-testid='venture-wizard-step2-addFounder'
        >
          Add Founder
        </Button>
      </div>
    </div>
  );
};

interface Step3Props {
  countSteps: number;
  stepNumber: number;
  showCustomAdditionalInfo?: boolean;
  additionalInfoCustomQuestions?: CustomQuestion[] | null;
  attachments: Map<string, Attachment>;
  setAttachments: React.Dispatch<React.SetStateAction<Map<string, Attachment>>>;
  files: Map<string, File>;
  setFiles: React.Dispatch<React.SetStateAction<Map<string, File>>>;
  tenantId: TenantId;
  goalIntegrationEnabled?: boolean;
}

interface WizardGoal {
  id: string;
  title: string;
  description: string;
  startDate: string | null;
  targetDate: string | null;
  timeframe: '3-months' | '12-months';
}

interface Step4Props {
  countSteps: number;
  stepNumber: number;
  tenantId: TenantId;
}

const Step3 = ({
  countSteps,
  stepNumber,
  showCustomAdditionalInfo,
  additionalInfoCustomQuestions,
  attachments,
  setAttachments,
  files,
  setFiles,
  tenantId,
  goalIntegrationEnabled = false,
}: Step3Props) => {
  const formAPI = useForm();
  const stateForm = formAPI.getState();
  const { rb } = useResourceBundles();

  const handleChangeField = (onChange: any, name: string, value: string) => {
    onChange(name, value);
  };

  // Subscribe to form state changes to get updated errors and submitFailed
  const [formState, setFormState] = useState(stateForm);
  useEffect(() => {
    const unsubscribe = formAPI.subscribe(
      (state) => {
        setFormState(state);
      },
      { errors: true, submitFailed: true },
    );
    return unsubscribe;
  }, [formAPI]);

  // Get errors and submitFailed from form state for validation display
  const formErrors = formState.errors || {};
  const submitFailed = formState.submitFailed || false;

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (['-', '.', ',', 'e', 'E', '+'].includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleUploadFileForQuestion = async (
    questionId: string,
    file: File,
  ): Promise<string> => {
    // Store file locally - will be uploaded after venture is created
    const attachment = getAttachmentFromFile(file);
    // Create temporary URL from file blob to show file as "uploaded" (no loading)
    const tempUrl = URL.createObjectURL(file);
    attachment.url = tempUrl;

    setFiles((prev) => {
      const newMap = new Map(prev);
      newMap.set(questionId, file);
      return newMap;
    });
    setAttachments((prev) => {
      const newMap = new Map(prev);
      newMap.set(questionId, attachment);
      return newMap;
    });
    // Return temporary marker to indicate file is selected (for validation)
    // Will be replaced with actual filePath after upload
    const tempMarker = `pending:${questionId}`;
    formAPI.change(questionId, tempMarker);
    return tempMarker;
  };

  const handleFileRemove = (questionId: string) => {
    // Clean up temporary blob URL if exists
    const attachment = attachments.get(questionId);
    if (attachment?.url && attachment.url.startsWith('blob:')) {
      URL.revokeObjectURL(attachment.url);
    }

    setFiles((prev) => {
      const newMap = new Map(prev);
      newMap.delete(questionId);
      return newMap;
    });
    setAttachments((prev) => {
      const newMap = new Map(prev);
      newMap.delete(questionId);
      return newMap;
    });
    formAPI.change(questionId, '');
  };

  return (
    <div>
      <StepHeader
        title='Additional Info'
        stepNumber={stepNumber}
        totalSteps={countSteps}
      />
      {showCustomAdditionalInfo && additionalInfoCustomQuestions?.length ? (
        additionalInfoCustomQuestions.map((question) => (
          <CustomFormField
            key={question.id}
            question={question}
            hasAccessToUpdate={true}
            onChange={(value) => formAPI.change(question.id, value)}
            value={formState.values?.[question.id] || ''}
            onUploadFile={
              question.type === CustomQuestionType.ATTACHMENT
                ? (file: File) => handleUploadFileForQuestion(question.id, file)
                : undefined
            }
            onFileRemove={
              question.type === CustomQuestionType.ATTACHMENT
                ? () => handleFileRemove(question.id)
                : undefined
            }
            attachment={
              question.type === CustomQuestionType.ATTACHMENT
                ? attachments.get(question.id)
                : undefined
            }
            error={formErrors[question.id]}
            submitFailed={submitFailed}
          />
        ))
      ) : (
        <>
          <FormGroup>
            <Field<string>
              name='howDidYouFind'
              component={TextFieldWysiwyg}
              label='How did you learn about our program?'
              placeholder='Type your answer here...'
              showCount
              maxLength={lengthField.wysiwygDisplayLimit}
              testid='venture-wizard-step3-find'
            />
          </FormGroup>
          <FormGroup>
            <Field<string>
              name='legally'
              component={FormRadioboxGroup}
              options={RADIO_SELECT}
              label='Are you legally registered business?*'
              testid='venture-wizard-step3-legally'
            />
          </FormGroup>
          <FormGroup>
            <Field<string>
              name='businessDescription'
              component={TextFieldWysiwyg}
              label='Please describe what your business does*'
              placeholder='Type your answer here...'
              testid='venture-wizard-step3-businessDescription'
              showCount
              maxLength={lengthField.wysiwygDisplayLimit}
            />
          </FormGroup>
          <FormGroup mobile={checkSizeTablet(450)}>
            <Field<string>
              name='numOfEmployees'
              component={TextFieldWrapper}
              type='number'
              label='Number Of Employees*'
              format={(val) => (val ? val.replace(/[^0-9]+/g, '') : val)}
              testid='venture-wizard-step3-numOfEmployees'
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyPress: handleKeyPress,
                },
              }}
            />
            <Field<string>
              name='isTech'
              component={FormCheckbox}
              label='Is Tech'
              testid='venture-wizard-step3-isTech'
            />
          </FormGroup>
          <CheckboxListOther
            name='currentProgress'
            options={DONE_ALREADY}
            onChange={(value) =>
              handleChangeField(formAPI.change, 'currentProgress', value)
            }
            text='What have you done already? (select all that apply)*'
            labelOther='Other'
            value={stateForm.values.currentProgress}
            testid='venture-wizard-step3-currentProgress'
          />
          <CheckboxListOther
            name='requiredSupport'
            options={SUPPORT_INTERESTED_RECEIVING}
            onChange={(value) =>
              handleChangeField(formAPI.change, 'requiredSupport', value)
            }
            text='What support are you seeking?*'
            labelOther='Other'
            value={stateForm.values.requiredSupport}
            testid='venture-wizard-step3-requiredSupport'
          />
          <FormGroup>
            <Field<string>
              name='marketSize'
              component={FormSelect}
              options={MARKET_SIZE}
              label='How big is the opportunity you see in the market for your business?*'
              testid='venture-wizard-step3-marketSize'
            />
          </FormGroup>
          <FormGroup>
            <Field<string>
              name='reasonToStartNow'
              component={TextFieldWysiwyg}
              label='Why now is the time to pursue this opportunity?*'
              placeholder='Type your answer here...'
              testid='venture-wizard-step3-reasonToStartNow'
              showCount
              maxLength={lengthField.wysiwygDisplayLimit}
            />
          </FormGroup>
          <FormGroup>
            <Field<string>
              name='plans'
              component={FormSelect}
              options={REASON_TO_START_NOW}
              label='What better describes your plans for this business?*'
              testid='venture-wizard-step3-plans'
            />
          </FormGroup>
          <FormGroup>
            <Field<string>
              name='growthRatio'
              component={FormSelect}
              options={GROWTH_RATIO}
              label='How quickly do you see your business growing?*'
              testid='venture-wizard-step3-growthRatio'
            />
          </FormGroup>
          <FormGroup>
            <Field<string>
              name='dedication'
              component={FormSelect}
              options={DEDICATION}
              label='Are you working on this business full-time?*'
              testid='venture-wizard-step3-dedication'
            />
          </FormGroup>
          {!goalIntegrationEnabled && (
            <>
              <FormGroup>
                <Field<string>
                  name='yearPlans'
                  component={TextFieldWysiwyg}
                  label='What are you hoping to accomplish in the next 12 months?*'
                  placeholder='Type your answer here...'
                  testid='venture-wizard-step3-yearPlans'
                  showCount
                  maxLength={lengthField.wysiwygDisplayLimit}
                />
              </FormGroup>
              <FormGroup>
                <Field<string>
                  name='obstacles'
                  component={TextFieldWysiwyg}
                  label='What obstacles do you foresee in reaching these goals?*'
                  placeholder='Type your answer here...'
                  testid='venture-wizard-step3-obstacles'
                  showCount
                  maxLength={lengthField.wysiwygDisplayLimit}
                />
              </FormGroup>
            </>
          )}
          <FormGroup>
            <Field<string>
              name='mentorshipPlans'
              component={TextFieldWysiwyg}
              label={`How do you think ${rb(
                'mentorship',
              )} is going to help you achieve these?*`}
              placeholder='Type your answer here...'
              testid='venture-wizard-step3-mentorshipPlans'
              showCount
              maxLength={lengthField.wysiwygDisplayLimit}
            />
          </FormGroup>
          <FormGroup>
            <Field<string>
              name='comments'
              component={TextFieldWysiwyg}
              testid='venture-wizard-step3-comments'
              label='Comments'
              placeholder='Type here...'
              showCount
              maxLength={lengthField.wysiwygDisplayLimit}
            />
          </FormGroup>
        </>
      )}
    </div>
  );
};

const Step4 = ({ countSteps, stepNumber, tenantId }: Step4Props) => {
  const classes = useStyles();
  const formAPI = useForm();
  const { enqueueSnackbar } = useSnackbar();

  const [modalOpen, setModalOpen] = useState(false);
  const [currentTimeframe, setCurrentTimeframe] = useState<
    '3-months' | '12-months'
  >('3-months');
  const [editingGoal, setEditingGoal] = useState<WizardGoal | null>(null);

  const [threeMonthGoals, setThreeMonthGoals] = useState<WizardGoal[]>([]);
  const [twelveMonthGoals, setTwelveMonthGoals] = useState<WizardGoal[]>([]);

  // Initialize goals from form state on mount (in case user goes back)
  useEffect(() => {
    const formState = formAPI.getState();
    if (
      formState.values.threeMonthGoals &&
      formState.values.threeMonthGoals.length > 0
    ) {
      setThreeMonthGoals(formState.values.threeMonthGoals);
    }
    if (
      formState.values.twelveMonthGoals &&
      formState.values.twelveMonthGoals.length > 0
    ) {
      setTwelveMonthGoals(formState.values.twelveMonthGoals);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModal = useCallback((timeframe: '3-months' | '12-months') => {
    setCurrentTimeframe(timeframe);
    setEditingGoal(null);
    setModalOpen(true);
  }, []);

  const handleEditGoal = useCallback((goal: WizardGoal) => {
    setEditingGoal(goal);
    setCurrentTimeframe(goal.timeframe);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingGoal(null);
  }, []);

  const handleSaveGoal = useCallback(
    (goalData: {
      title: string;
      description: string;
      startDate: string | null;
      targetDate: string | null;
    }) => {
      if (editingGoal) {
        // Edit existing goal
        const updatedGoal: WizardGoal = {
          ...editingGoal,
          title: goalData.title,
          description: goalData.description,
          startDate: goalData.startDate,
          targetDate: goalData.targetDate,
        };

        if (editingGoal.timeframe === '3-months') {
          setThreeMonthGoals((prev) =>
            prev.map((g) => (g.id === editingGoal.id ? updatedGoal : g)),
          );
        } else {
          setTwelveMonthGoals((prev) =>
            prev.map((g) => (g.id === editingGoal.id ? updatedGoal : g)),
          );
        }
      } else {
        // Create new goal
        const newGoal: WizardGoal = {
          id: Math.random().toString(36).substr(2, 9),
          title: goalData.title,
          description: goalData.description,
          startDate: goalData.startDate,
          targetDate: goalData.targetDate,
          timeframe: currentTimeframe,
        };

        if (currentTimeframe === '3-months') {
          if (threeMonthGoals.length >= 3) {
            enqueueSnackbar('You can only create up to 3 goals for 3 months', {
              variant: 'warning',
            });
            return;
          }
          setThreeMonthGoals((prev) => [...prev, newGoal]);
        } else {
          if (twelveMonthGoals.length >= 3) {
            enqueueSnackbar('You can only create up to 3 goals for 12 months', {
              variant: 'warning',
            });
            return;
          }
          setTwelveMonthGoals((prev) => [...prev, newGoal]);
        }
      }

      handleCloseModal();
    },
    [
      editingGoal,
      currentTimeframe,
      threeMonthGoals,
      twelveMonthGoals,
      enqueueSnackbar,
      handleCloseModal,
    ],
  );

  const handleDeleteGoal = useCallback(
    (goalId: string) => () => {
      setThreeMonthGoals((prev) => prev.filter((g) => g.id !== goalId));
      setTwelveMonthGoals((prev) => prev.filter((g) => g.id !== goalId));
    },
    [],
  );

  // Store goals in form state
  useEffect(() => {
    formAPI.change('threeMonthGoals', threeMonthGoals);
    formAPI.change('twelveMonthGoals', twelveMonthGoals);
  }, [threeMonthGoals, twelveMonthGoals, formAPI]);

  return (
    <div>
      <StepHeader
        title='Goals'
        stepNumber={stepNumber}
        totalSteps={countSteps}
      />
      <div className={classes.goalDescription}>
        <Text variant='normal'>
          Please provide specific Venture goals that you have for the next 3 and
          12 months below. This step is optional
        </Text>
      </div>

      {/* 3 Months Goals Section */}
      <div className={classes.goalsBlock}>
        <Typography variant='h3' className={classes.title}>
          Create up to 3 goals for the next 3 months
        </Typography>
        <MaterialButton
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal('3-months')}
          disabled={threeMonthGoals.length >= 3}
          className={classes.goalButton}
        >
          Goal
        </MaterialButton>
        {threeMonthGoals.length > 0 && (
          <div className={classes.goalsTable}>
            {threeMonthGoals.map((goal) => (
              <div key={goal.id} className={classes.goalRow}>
                <div className={classes.goalName}>
                  <Text variant='normal'>{goal.title}</Text>
                </div>
                <div className={classes.goalActions}>
                  <IconButton onClick={() => handleEditGoal(goal)} size='small'>
                    <EditIcon />
                  </IconButton>
                  <ConfirmButton
                    title='Delete Goal?'
                    body='Are you sure you want to delete this goal?'
                    successProps={{
                      btnLabel: 'Delete',
                      onSuccess: handleDeleteGoal(goal.id),
                    }}
                    cancelProps={{
                      btnLabel: 'Cancel',
                    }}
                    buttonRenderer={({ onClick }) => (
                      <IconButton onClick={onClick} size='small'>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 12 Months Goals Section */}
      <div className={classes.goalsBlock}>
        <Typography variant='h3' className={classes.title}>
          Create up to 3 goals for the next 12 months
        </Typography>
        <MaterialButton
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal('12-months')}
          disabled={twelveMonthGoals.length >= 3}
          className={classes.goalButton}
        >
          Goal
        </MaterialButton>
        {twelveMonthGoals.length > 0 && (
          <div className={classes.goalsTable}>
            {twelveMonthGoals.map((goal) => (
              <div key={goal.id} className={classes.goalRow}>
                <div className={classes.goalName}>
                  <Text variant='normal'>{goal.title}</Text>
                </div>
                <div className={classes.goalActions}>
                  <IconButton onClick={() => handleEditGoal(goal)} size='small'>
                    <EditIcon />
                  </IconButton>
                  <ConfirmButton
                    title='Delete Goal?'
                    body='Are you sure you want to delete this goal?'
                    successProps={{
                      btnLabel: 'Delete',
                      onSuccess: handleDeleteGoal(goal.id),
                    }}
                    cancelProps={{
                      btnLabel: 'Cancel',
                    }}
                    buttonRenderer={({ onClick }) => (
                      <IconButton onClick={onClick} size='small'>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Goal Form Modal */}
      <GoalFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveGoal}
        initialData={editingGoal}
        tenantId={tenantId}
      />
    </div>
  );
};

interface Step5Props {
  countSteps: number;
  stepNumber: number;
  consents: ConsentType[];
}

const Step5 = ({ consents, countSteps, stepNumber }: Step5Props) => {
  const classes = useStyles();

  return (
    <div>
      <StepHeader
        title='Commitment'
        stepNumber={stepNumber}
        totalSteps={countSteps}
      />
      {consents.map((consent) => (
        <div className={classes.consent} key={consent.id}>
          <div
            className={classes.consent}
            dangerouslySetInnerHTML={createMarkup(consent.body)}
          />
          <Field<string>
            name={`field-${consent.id}`}
            component={FormCheckbox}
            label='I accept'
            classNameLabel={classes.checkboxLabel}
            testid='venture-wizard-step4-accept'
          />
        </div>
      ))}
    </div>
  );
};

interface Step6Props {
  countSteps: number;
  stepNumber: number;
  tenantId: TenantId;
  ventureId: VentureId | null;
  attachments: Attachment[];
  handleFileUpload: (file: File) => Promise<void>;
  handleFileRemove: (index: number) => void;
}

const Step6 = ({
  countSteps,
  stepNumber,
  tenantId,
  ventureId,
  attachments,
  handleFileUpload,
  handleFileRemove,
}: Step6Props) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [founders, setFounders] = useState<FounderName[]>([]);
  const [isLoading, setLoading] = useState<boolean>(true);

  const handleSelectFile = (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File,
  ) => {
    handleFileUpload(file);
    e.target.value = '';
  };

  useEffect(() => {
    async function getFounders() {
      if (ventureId) {
        setLoading(true);
        try {
          const founders = await foundersAPI.getPublicFounderNames(
            ventureId,
            tenantId,
          );
          setFounders(founders);
        } catch (e: any) {
          const messageError =
            e.response?.data?.message || 'Internal server error';

          enqueueSnackbar('An error occurred while enrollment', {
            variant: 'error',
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            style: { whiteSpace: 'pre-line' },
          });
        } finally {
          setLoading(false);
        }
      }
    }

    getFounders();
  }, [enqueueSnackbar, tenantId, ventureId]);

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  return (
    <div>
      <StepHeader
        title='Thank you. We are Almost Done!'
        stepNumber={stepNumber}
        totalSteps={countSteps}
      />
      <Text variant='normal' className={classes.intro}>
        In this optional step, you're invited to upload photos of your founding
        team and your venture's pitch deck. Uploading photos of your founding
        team aids in completing our database, streamlining data entry.
        Additionally, your pitch deck offers valuable insights for a thorough
        assessment of your application.
      </Text>
      {founders.length > 0 && (
        <div className={classes.sectionBlock}>
          <Typography variant='h3' className={classes.title}>
            Upload Founder Photos (optional)
          </Typography>
          <div className={classes.founderNamesList}>
            {founders?.map((founder, index) => (
              <UploadFounderPhoto
                key={index}
                data={founder}
                tenantId={tenantId}
                index={index}
              />
            ))}
          </div>
        </div>
      )}
      <div className={classes.sectionBlock}>
        <Typography variant='h3' className={classes.title}>
          Upload Your Pitch Deck (optional)
        </Typography>
        <AttachFilesButton
          text='Upload Deck'
          variant='outlined'
          onChange={handleSelectFile}
        />
        <div className={classes.attachmentsList}>
          {attachments.map((attachment, attachIndex) => (
            <div key={attachIndex} className={classes.attachment}>
              <AttachmentCard
                attachment={attachment}
                loading={!attachment.url}
                onRemove={() => handleFileRemove(attachIndex)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface UploadFounderPhotoProps {
  data: FounderName;
  tenantId: TenantId;
  index: number;
}

const UploadFounderPhoto = ({
  data,
  tenantId,
  index,
}: UploadFounderPhotoProps) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [logoUrl, setLogoUrl] = useState<string | null>(data.logoUrl);

  const validateLogoFile = (logo: File) => {
    const fileName = logo.name;
    if (fileName.length > lengthField.logoFileName) {
      enqueueSnackbar(
        `The maximum length of the file name is ${lengthField.logoFileName} characters`,
        {
          variant: 'error',
        },
      );
      return false;
    }
    return true;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const photoFile = e.target.files ? e.target.files[0] : null;
    if (photoFile && validateLogoFile(photoFile)) {
      const response = await foundersAPI.updatePublicAvatar(
        tenantId,
        data.founderId,
        photoFile,
      );
      setLogoUrl(response.logoUrl);
    }
  };

  // This is a hack to validate the same file again
  const handleFileUploadClick = (e: React.MouseEvent<HTMLInputElement>) =>
    ((e.target as HTMLInputElement).value = '');

  return (
    <div className={classes.uploadPhotoWrapper}>
      <Avatar
        src={logoUrl}
        name={`${data.firstName?.[0]}${data.lastName?.[0]}`}
        size='40'
      ></Avatar>
      <Text variant='normal'>
        {data.firstName} {data.lastName}
      </Text>
      <MaterialButton
        data-testid={`upload-photo-button-${index}`}
        variant='outlined'
        component='label'
        color='primary'
        startIcon={<ExportIcon fontSize='inherit' />}
      >
        Upload photo
        <input
          type='file'
          accept='image/*'
          id={`upload-photo-input-${data.founderId}`}
          hidden
          onChange={handleFileUpload}
          onClick={handleFileUploadClick}
        />
      </MaterialButton>
    </div>
  );
};

function VentureApplicationsWizardForm({
  onSubmit,
  tenantId,
  isLoading,
  consents,
  showCustomAdditionalInfo,
  additionalInfoCustomQuestions,
  intro,
  venture,
  showSuccess,
  noteAttachments,
  handleNoteFileUpload,
  handleNoteFileRemove,
  createNote,
  attachments,
  setAttachments,
  files,
  setFiles,
  founderCommitmentEnabled = false,
  goalIntegrationEnabled = false,
  showFounderCustomQuestions,
  founderCustomQuestions,
  founderAttachments,
  setFounderAttachments,
  founderFiles,
  setFounderFiles,
  showPrefillModal = false,
  onPrefillComplete,
  onPrefillDismiss,
  prefillData,
}: Props) {
  const withConsents = consents.length > 0 && founderCommitmentEnabled;
  const withGoals = goalIntegrationEnabled;

  // Calculate count steps based on features enabled
  let countSteps = 4; // Base: Business Info, Founders, Additional Info, Upload
  if (withGoals) countSteps += 1; // Add Goals step
  if (withConsents) countSteps += 1; // Add Consent step

  // Compute step numbers for conditional steps
  let nextStep = 4;
  const goalsStepNum = nextStep;
  if (withGoals) nextStep++;
  const commitmentStepNum = nextStep;
  if (withConsents) nextStep++;
  const uploadStepNum = nextStep;

  return (
    <>
      {showPrefillModal && onPrefillComplete && onPrefillDismiss && (
        <PrefillModal
          open={showPrefillModal}
          onClose={onPrefillDismiss}
          onPrefillComplete={onPrefillComplete}
          tenantId={tenantId}
          wizardType='venture'
        />
      )}
      <Wizard
        // @ts-expect-error
        onSubmit={onSubmit}
        initialValues={prefillData || undefined}
        tenantId={tenantId}
        isLoading={isLoading}
        showCustomAdditionalInfo={showCustomAdditionalInfo}
        additionalInfoCustomQuestions={additionalInfoCustomQuestions}
        consents={consents}
        showSuccess={showSuccess}
        onDone={createNote}
        founderCommitmentEnabled={founderCommitmentEnabled}
        goalIntegrationEnabled={goalIntegrationEnabled}
        showFounderCustomQuestions={showFounderCustomQuestions}
        founderCustomQuestions={founderCustomQuestions}
      >
        <Step1
          countSteps={countSteps}
          stepNumber={1}
          intro={intro}
          tenantId={tenantId}
        />
        <Step2
          countSteps={countSteps}
          stepNumber={2}
          showFounderCustomQuestions={showFounderCustomQuestions}
          founderCustomQuestions={founderCustomQuestions}
          founderAttachments={founderAttachments}
          setFounderAttachments={setFounderAttachments}
          founderFiles={founderFiles}
          setFounderFiles={setFounderFiles}
        />
        <Step3
          countSteps={countSteps}
          stepNumber={3}
          showCustomAdditionalInfo={showCustomAdditionalInfo}
          additionalInfoCustomQuestions={additionalInfoCustomQuestions}
          attachments={attachments}
          setAttachments={setAttachments}
          files={files}
          setFiles={setFiles}
          tenantId={tenantId}
          goalIntegrationEnabled={goalIntegrationEnabled}
        />
        {withGoals && (
          <Step4
            countSteps={countSteps}
            stepNumber={goalsStepNum}
            tenantId={tenantId}
          />
        )}
        {withConsents && (
          <Step5
            consents={consents}
            countSteps={countSteps}
            stepNumber={commitmentStepNum}
          />
        )}
        <Step6
          countSteps={countSteps}
          stepNumber={uploadStepNum}
          tenantId={tenantId}
          ventureId={venture?.id || null}
          attachments={noteAttachments}
          handleFileUpload={handleNoteFileUpload}
          handleFileRemove={handleNoteFileRemove}
        />
      </Wizard>
    </>
  );
}

export default VentureApplicationsWizardForm;
